import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import { Download, Calendar } from "lucide-react";
import { toCSV, downloadCSV } from "../lib/format.js";
import ConfigurableTable, { ColumnConfigButton, useColumnConfig } from "../components/ConfigurableTable.jsx";
import SavedViewsBar from "../components/SavedViewsBar.jsx";
import { registerView, buildReportColumns } from "../lib/columnConfig.js";

// Each report: title, columns (key, label, kind: text|date|number|status|pct),
// optional supportsDateRange.
const REPORTS = [
  {
    key: "open-cases",
    title: "Open Service Cases",
    cols: [
      { key: "number", label: "Case #" },
      { key: "customer", label: "Customer" },
      { key: "vehicle", label: "Vehicle" },
      { key: "brand", label: "Brand" },
      { key: "branch", label: "Branch" },
      { key: "status", label: "Status" },
      { key: "responsible", label: "Responsible" },
      { key: "opened_at", label: "Opened on", kind: "date" },
      { key: "deadline", label: "Deadline", kind: "date" },
      { key: "days_open", label: "Days open", kind: "number" },
      { key: "feedback_status", label: "Feedback" },
    ],
    supportsDateRange: true,
  },
  {
    key: "no-feedback",
    title: "Cases without customer feedback",
    cols: [
      { key: "number", label: "Case #" },
      { key: "customer", label: "Customer" },
      { key: "vehicle", label: "Vehicle" },
      { key: "branch", label: "Branch" },
      { key: "status", label: "Status" },
      { key: "hours_without_contact", label: "Hours without contact", kind: "number" },
    ],
    params: { threshold: 48 },
  },
  {
    key: "overdue-tasks",
    title: "Overdue tasks",
    cols: [
      { key: "number", label: "Task #" },
      { key: "title", label: "Title" },
      { key: "responsible", label: "Responsible" },
      { key: "deadline", label: "Deadline", kind: "date" },
      { key: "days_overdue", label: "Days overdue", kind: "number" },
      { key: "escalation_level", label: "Escalation", kind: "number" },
    ],
  },
  {
    key: "parts-eta",
    title: "Parts pending by ETA",
    cols: [
      { key: "pr_number", label: "PR #" },
      { key: "part_number", label: "Part #" },
      { key: "description", label: "Description" },
      { key: "vehicle", label: "Vehicle" },
      { key: "customer", label: "Customer" },
      { key: "eta", label: "ETA", kind: "date" },
      { key: "overdue", label: "Overdue", kind: "bool" },
    ],
  },
  {
    key: "parts-received-no-schedule",
    title: "Parts received but not scheduled",
    cols: [
      { key: "pr_number", label: "PR #" },
      { key: "case_number", label: "Case #" },
      { key: "customer", label: "Customer" },
      { key: "vehicle", label: "Vehicle" },
      { key: "age_days", label: "Age (days)", kind: "number" },
    ],
  },
  {
    key: "advisor-performance",
    title: "Performance by Service Advisor",
    cols: [
      { key: "advisor", label: "Advisor" },
      { key: "case_count", label: "Total cases", kind: "number" },
      { key: "closed_count", label: "Closed", kind: "number" },
      { key: "avg_days_to_close", label: "Avg days to close", kind: "number" },
      { key: "feedback_compliance_pct", label: "Feedback compliance %", kind: "pct" },
      { key: "escalations", label: "Escalations", kind: "number" },
    ],
    supportsDateRange: true,
  },
  {
    key: "branch-performance",
    title: "Performance by Branch",
    cols: [
      { key: "branch", label: "Branch" },
      { key: "cases_opened", label: "Cases opened", kind: "number" },
      { key: "cases_closed", label: "Cases closed", kind: "number" },
      { key: "avg_case_duration_days", label: "Avg case duration (days)", kind: "number" },
      { key: "escalations", label: "Escalations", kind: "number" },
      { key: "feedback_compliance_pct", label: "Feedback compliance %", kind: "pct" },
    ],
    supportsDateRange: true,
  },
  {
    key: "status-time",
    title: "Average time per status",
    cols: [
      { key: "status", label: "Status" },
      { key: "avg_hours", label: "Avg hours", kind: "number" },
      { key: "occurrences", label: "Occurrences", kind: "number" },
    ],
  },
  {
    key: "escalations-by-person",
    title: "Escalations by person",
    cols: [
      { key: "user", label: "User" },
      { key: "escalations", label: "Escalations", kind: "number" },
    ],
  },
  {
    key: "warranty-by-status",
    title: "Warranty claims by status",
    cols: [
      { key: "status", label: "Status" },
      { key: "count", label: "Count", kind: "number" },
      { key: "estimated_total", label: "Estimated (MZN)", kind: "number" },
      { key: "approved_total", label: "Approved (MZN)", kind: "number" },
      { key: "avg_age_days", label: "Avg age (days)", kind: "number" },
    ],
  },
  {
    key: "mpm-expiring",
    title: "Maintenance plans — expiry forecast",
    cols: [
      { key: "number", label: "Plan #" },
      { key: "customer", label: "Customer" },
      { key: "vehicle", label: "Vehicle" },
      { key: "brand", label: "Brand" },
      { key: "plan_name", label: "Plan" },
      { key: "valid_to", label: "Valid to" },
      { key: "days_left", label: "Days left", kind: "number" },
      { key: "bucket", label: "Bucket" },
      { key: "consumed", label: "Consumed", kind: "number" },
      { key: "total", label: "Total", kind: "number" },
    ],
  },
  {
    key: "fuel-consumption",
    title: "Combustível por SOM",
    supportsDateRange: true,
    hasTotals: true,
    cols: [
      { key: "fuel_request_number", label: "FUEL #" },
      { key: "service_case_number", label: "SOM #" },
      { key: "vehicle_registration", label: "Matrícula" },
      { key: "brand", label: "Marca" },
      { key: "branch", label: "Dealer" },
      { key: "fuel_type", label: "Combustível" },
      { key: "quantity_requested_litres", label: "Qt. pedida (L)", kind: "number" },
      { key: "quantity_approved_litres", label: "Qt. aprovada (L)", kind: "number" },
      { key: "quantity_used_litres", label: "Qt. usada (L)", kind: "number" },
      { key: "status", label: "Estado" },
      { key: "approved_by_name", label: "Aprovado por" },
      { key: "supplier_requisition_number", label: "Requisição" },
      { key: "created_at", label: "Data", kind: "date" },
    ],
  },
  {
    key: "bps-by-dealer",
    title: "Bate-chapas por dealer",
    supportsDateRange: true,
    cols: [
      { key: "dealer", label: "Dealer" },
      { key: "cases_opened", label: "Casos abertos", kind: "number" },
      { key: "cases_delivered", label: "Entregues", kind: "number" },
      { key: "cases_closed", label: "Fechados", kind: "number" },
      { key: "cases_rejected", label: "Rejeitados", kind: "number" },
      { key: "rejection_rate_pct", label: "Taxa rejeição %", kind: "pct" },
    ],
  },
  {
    key: "bps-by-insurance",
    title: "Bate-chapas por seguradora",
    supportsDateRange: true,
    cols: [
      { key: "insurance", label: "Seguradora" },
      { key: "dealer", label: "Dealer" },
      { key: "total", label: "Total casos", kind: "number" },
      { key: "rejected", label: "Rejeitados", kind: "number" },
      { key: "rejection_rate_pct", label: "Taxa rejeição %", kind: "pct" },
    ],
  },
  {
    key: "bps-by-work-type",
    title: "Bate-chapas por tipo de obra",
    supportsDateRange: true,
    cols: [
      { key: "work_type_label", label: "Tipo de obra" },
      { key: "total", label: "Casos", kind: "number" },
      { key: "delivered", label: "Entregues", kind: "number" },
      { key: "billed", label: "Facturados", kind: "number" },
      { key: "avg_workshop_days", label: "Tempo oficina médio (dias)", kind: "number" },
      { key: "avg_bps0_hours", label: "BPS 0 Recepção (h)", kind: "number" },
      { key: "avg_bps1_hours", label: "BPS 1 Chapa (h)", kind: "number" },
      { key: "avg_bps2_hours", label: "BPS 2 Preparação (h)", kind: "number" },
      { key: "avg_bps3_hours", label: "BPS 3 Pintura (h)", kind: "number" },
      { key: "avg_bps4_hours", label: "BPS 4 Acabamento (h)", kind: "number" },
    ],
  },
  {
    key: "bps-by-invoice-type",
    title: "Bate-chapas — facturação por tipo de factura",
    supportsDateRange: true,
    cols: [
      { key: "invoice_type_label", label: "Tipo de factura" },
      { key: "invoices", label: "Facturas", kind: "number" },
      { key: "mixed_invoices", label: "Mistas (2 tipos)", kind: "number" },
      { key: "by_work_type", label: "Por tipo de obra" },
    ],
  },
  {
    key: "peb-lost-sales",
    title: "PEB — Lost sales",
    supportsDateRange: true,
    cols: [
      { key: "number", label: "PEB #" },
      { key: "customer_name", label: "Cliente" },
      { key: "customer_phone", label: "Telefone" },
      { key: "branch", label: "Dealer" },
      { key: "reason", label: "Motivo" },
      { key: "line_count", label: "Linhas", kind: "number" },
      { key: "lost_at", label: "Data perda", kind: "date" },
    ],
  },
  {
    key: "peb-response-time",
    title: "PEB — Tempo de resposta (parts advisor)",
    supportsDateRange: true,
    cols: [
      { key: "advisor_name", label: "Parts advisor" },
      { key: "quotations_sent", label: "Cotações enviadas", kind: "number" },
      { key: "avg_response_hours", label: "Tempo médio (h)", kind: "number" },
    ],
  },
  {
    key: "peb-conversion",
    title: "PEB — Taxa de conversão cotação → encomenda",
    supportsDateRange: true,
    cols: [
      { key: "metric", label: "Métrica" },
      { key: "sent", label: "Cotações enviadas", kind: "number" },
      { key: "converted", label: "Convertidas", kind: "number" },
      { key: "lost", label: "Perdidas", kind: "number" },
      { key: "expired", label: "Expiradas", kind: "number" },
      { key: "conversion_pct", label: "Conversão %", kind: "pct" },
    ],
  },
  {
    key: "peb-frequent-unstocked",
    title: "PEB — Peças pedidas repetidamente",
    cols: [
      { key: "part_number", label: "Part #" },
      { key: "description", label: "Descrição" },
      { key: "request_count", label: "Nº pedidos", kind: "number" },
      { key: "unique_customers", label: "Clientes únicos", kind: "number" },
    ],
  },
  {
    key: "opportunities-pipeline",
    title: "CRM — Pipeline comercial",
    supportsDateRange: true,
    cols: [
      { key: "state", label: "Estado" },
      { key: "count", label: "Contagem", kind: "number" },
    ],
  },
  {
    key: "complaints-sla",
    title: "CRM — Reclamações SLA por categoria",
    supportsDateRange: true,
    cols: [
      { key: "category", label: "Categoria" },
      { key: "total", label: "Total", kind: "number" },
      { key: "open", label: "Abertas", kind: "number" },
      { key: "resolved", label: "Resolvidas", kind: "number" },
      { key: "overdue", label: "Em atraso", kind: "number" },
      { key: "avg_resolution_hours", label: "Tempo médio (h)", kind: "number" },
    ],
  },
  {
    key: "satisfaction-overview",
    title: "Satisfação — Overview (NPS/CSI/CES)",
    supportsDateRange: true,
    cols: [
      { key: "department", label: "Departamento" },
      { key: "responses", label: "Respostas", kind: "number" },
      { key: "nps_score", label: "NPS", kind: "number" },
      { key: "detractors", label: "Detractors", kind: "number" },
      { key: "csi_avg", label: "CSI médio", kind: "number" },
      { key: "ces_avg", label: "CES médio", kind: "number" },
    ],
  },
  {
    key: "peb-monthly",
    title: "Encomendas de Balcão",
    supportsDateRange: true,
    cols: [
      { key: "month", label: "Mês" },
      { key: "created", label: "Criadas", kind: "number" },
      { key: "collected", label: "Levantadas", kind: "number" },
      { key: "closed", label: "Fechadas", kind: "number" },
      { key: "conversion_pct", label: "Conversão %", kind: "pct" },
      { key: "total_lines", label: "Total linhas", kind: "number" },
      { key: "avg_customs_days", label: "Alfândegas médio (dias)", kind: "number" },
    ],
  },
  {
    key: "salesperson-performance",
    title: "Desempenho de vendedores (VRQ)",
    cols: [
      { key: "salesperson_name", label: "Vendedor" },
      { key: "total", label: "Total VRQs", kind: "number" },
      { key: "open", label: "Abertas", kind: "number" },
      { key: "converted", label: "Convertidas em VR", kind: "number" },
      { key: "lost", label: "Perdidas", kind: "number" },
      { key: "conversion_rate_pct", label: "Conversão %", kind: "pct" },
      { key: "lost_rate_pct", label: "Perdidas %", kind: "pct" },
      { key: "avg_cycle_days", label: "Ciclo médio (dias)", kind: "number" },
      { key: "avg_follow_ups_per_closed", label: "Follow-ups médios", kind: "number" },
      { key: "top_lost_reasons", label: "Top motivos perdidas", kind: "lost_reasons" },
    ],
    supportsDateRange: true,
    responseKey: "rows",
  },
  {
    key: "vehicle-population",
    title: "População de viaturas",
    cols: [
      { key: "brand", label: "Marca" },
      { key: "model", label: "Modelo" },
      { key: "generation", label: "Geração" },
      { key: "total", label: "Nº viaturas", kind: "number" },
      { key: "top_issues", label: "Top queixas (SOMs)", kind: "lost_reasons" },
    ],
    responseKey: "flat",
    // Flatten the nested {brand, models:[{generations:[]}]} into a flat table
    // so the shared tabular renderer can show it. Done client-side below.
    kind: "vehicle_population",
  },
  {
    key: "logistics-phase-times",
    title: "Logística — tempos por fase",
    cols: [
      { key: "status", label: "Estado" },
      { key: "n", label: "Nº viaturas", kind: "number" },
      { key: "avg_days", label: "Média (dias)", kind: "number" },
      { key: "median_days", label: "Mediana (dias)", kind: "number" },
      { key: "p90_days", label: "P90 (dias)", kind: "number" },
      { key: "max_days", label: "Máx (dias)", kind: "number" },
      { key: "min_days", label: "Min (dias)", kind: "number" },
    ],
    responseKey: "aggregates",
  },
];

// Register each report as a configurable view so users can hide/reorder columns
// and save custom views per report.
REPORTS.forEach((r) => {
  registerView(`report:${r.key}`, {
    columns: buildReportColumns(r.cols),
    default: r.cols.map((c) => c.key),
    defaultSort: null,
  });
});

function isIsoDate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v);
}

function fmtDateDMY(v) {
  if (v == null || v === "") return "";
  if (isIsoDate(v) || /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  return v;
}

function fmtCell(val, kind) {
  if (val == null) return "—";
  if (kind === "date") return val ? fmtDateDMY(val) : "—";
  if (kind === "pct") return `${val}%`;
  if (kind === "bool") return val ? "Yes" : "No";
  return String(val);
}

export default function ReportsPage() {
  const { t } = useI18n();
  const [active, setActive] = useState("open-cases");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const conf = REPORTS.find((r) => r.key === active);
  const viewKey = `report:${active}`;
  const colConfig = useColumnConfig(viewKey);

  useEffect(() => {
    setLoading(true);
    setRows([]);
    const params = new URLSearchParams(conf.params || {});
    if (conf.supportsDateRange) {
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
    }
    api.get(`/reports/${active}?${params}`).then((r) => {
      // Vehicle-population comes nested — flatten into (brand, model, generation, total, top_issues) rows.
      if (conf.kind === "vehicle_population") {
        const flat = [];
        for (const b of r.data?.rows || []) {
          for (const m of b.models) {
            for (const g of m.generations) {
              flat.push({
                brand: b.brand,
                model: m.model,
                generation: g.generation,
                total: g.total,
                top_issues: (g.top_issues || []).map((t) => ({ reason: t.keyword, count: t.count })),
              });
            }
          }
        }
        setRows(flat);
        return;
      }
      // Reports that wrap items in an object (e.g. { rows, totals }) declare
      // `responseKey` on the config — unwrap it here.
      const payload = conf.responseKey ? (r.data?.[conf.responseKey] ?? []) : r.data;
      setRows(Array.isArray(payload) ? payload : []);
    }).finally(() => setLoading(false));
  }, [active, dateFrom, dateTo, conf.supportsDateRange, conf.params, conf.responseKey, conf.kind]);

  const summary = useMemo(() => {
    const total = rows.length;
    const overdue = rows.filter((r) => r._overdue).length;
    return { total, overdue };
  }, [rows]);

  const fuelTotals = useMemo(() => {
    if (active !== "fuel-consumption") return null;
    return rows.reduce(
      (acc, r) => ({
        req: acc.req + Number(r.quantity_requested_litres || 0),
        appr: acc.appr + Number(r.quantity_approved_litres || 0),
        used: acc.used + Number(r.quantity_used_litres || 0),
      }),
      { req: 0, appr: 0, used: 0 }
    );
  }, [active, rows]);

  const dateRangeLabel = useMemo(() => {
    if (!conf.supportsDateRange) return "All dates";
    if (!dateFrom && !dateTo) return "All dates";
    const from = dateFrom ? fmtDateDMY(dateFrom) : "earliest";
    const to = dateTo ? fmtDateDMY(dateTo) : "now";
    return `${from} → ${to}`;
  }, [dateFrom, dateTo, conf.supportsDateRange]);

  const exportCsv = () => {
    const headers = conf.cols.map((c) => ({ key: c.key, label: c.label }));
    // Format each row's date columns
    const formatted = rows.map((r) => {
      const out = {};
      for (const c of conf.cols) {
        const raw = r[c.key];
        out[c.key] = c.kind === "date" ? fmtDateDMY(raw) : c.kind === "bool" ? (raw ? "Yes" : "No") : raw == null ? "" : raw;
      }
      return out;
    });
    const summaryRow = {
      [headers[0].key]: `# ${conf.title} — Total: ${summary.total} | Overdue: ${summary.overdue} | Date range: ${dateRangeLabel} | Generated: ${fmtDateDMY(new Date().toISOString())}`,
    };
    const csv = toCSV([summaryRow, ...formatted], headers);
    downloadCSV(`${active}.csv`, csv);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E]">{t("reports")}</h1>
          <p className="text-sm text-[#6C757D] mt-1">Operational and performance reports.</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="text-sm bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-2 rounded-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="export-csv"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Sidebar list */}
        <div className="bg-white border border-gray-200 rounded-md p-2 space-y-0.5 h-fit">
          {REPORTS.map((r) => (
            <button
              key={r.key}
              onClick={() => setActive(r.key)}
              className={`w-full text-left px-3 py-1.5 rounded-sm text-sm transition-colors ${
                active === r.key ? "bg-[#1F3864] text-white" : "hover:bg-[#F4F6F8] text-[#1A1A2E]"
              }`}
              data-testid={`report-${r.key}`}
            >
              {r.title}
            </button>
          ))}
        </div>

        {/* Main report */}
        <div className="md:col-span-3 space-y-3">
          {/* Date range bar (if applicable) */}
          {conf.supportsDateRange && (
            <div className="bg-white border border-gray-200 rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="date-range-bar">
              <Calendar className="w-4 h-4 text-[#6C757D] mb-2" />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">From</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="block mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm"
                  data-testid="report-date-from"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">To</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="block mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm"
                  data-testid="report-date-to"
                />
              </label>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-[#1F3864] hover:underline pb-2" data-testid="report-date-clear">
                  Clear range
                </button>
              )}
            </div>
          )}

          {/* Summary statistics row */}
          <div className="bg-white border border-gray-200 rounded-md p-3" data-testid="report-summary">
            <div className="grid grid-cols-3 gap-3">
              <SummaryStat label="Total records" value={summary.total} testid="summary-total" />
              <SummaryStat label="Overdue / critical" value={summary.overdue} accent={summary.overdue > 0 ? "red" : "grey"} testid="summary-overdue" />
              <SummaryStat label="Date range applied" value={dateRangeLabel} text testid="summary-range" />
            </div>
            {fuelTotals && (
              <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100" data-testid="fuel-totals-row">
                <SummaryStat label="Total pedida (L)" value={fuelTotals.req.toFixed(1)} testid="fuel-total-req" />
                <SummaryStat label="Total aprovada (L)" value={fuelTotals.appr.toFixed(1)} testid="fuel-total-appr" />
                <SummaryStat label="Total usada (L)" value={fuelTotals.used.toFixed(1)} testid="fuel-total-used" />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 bg-[#F4F6F8] flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-sm font-bold text-[#1A1A2E]" data-testid="report-title">{conf.title}</h2>
              <div className="flex items-center gap-2">
                {summary.overdue > 0 && (
                  <span className="text-[10px] uppercase tracking-widest bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 px-2 py-0.5 rounded-sm font-semibold">
                    {summary.overdue} row{summary.overdue === 1 ? "" : "s"} require attention
                  </span>
                )}
                <ColumnConfigButton config={colConfig} viewKey={viewKey} />
              </div>
            </div>
            <div className="px-3 py-2 border-b border-gray-100 bg-[#FAFBFC]">
              <SavedViewsBar viewKey={viewKey} config={colConfig} />
            </div>
            {loading ? (
              <div className="p-6 text-center text-[#6C757D] text-sm">A carregar…</div>
            ) : (
              <ConfigurableTable
                viewKey={viewKey}
                config={colConfig}
                rows={rows}
                getRowId={(_r, i) => i}
                emptyMessage="Sem dados."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, accent, text, testid }) {
  const color = accent === "red"
    ? "text-[#DC3545]"
    : accent === "grey"
      ? "text-[#6C757D]"
      : "text-[#1A1A2E]";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className={`${text ? "text-sm font-medium" : "font-heading text-2xl font-bold"} ${color}`}>{value}</div>
    </div>
  );
}
